/* eslint-disable react-refresh/only-export-components -- the paths, hook and provider are one module */
import { createContext, useContext, type ReactNode } from 'react'

/**
 * Where the proposal pages live. The SAME pages are mounted twice while the CRM is being built
 * (ENT-565): at `/proposals/*` (today's home) and at `/crm/proposals/*` (inside the CRM tool). Each
 * page builds its links and redirects from these paths, so a user who opened a proposal in the CRM
 * stays in the CRM. When the CRM is fully wired, `/proposals/*` is deleted and `CRM_PROPOSAL_PATHS`
 * becomes the only set.
 */
export interface ProposalPaths {
  list: '/proposals' | '/crm/proposals'
  create: '/proposals/new' | '/crm/proposals/new'
  approvals: '/proposals/approvals' | '/crm/proposals/approvals'
  detail: '/proposals/$proposalId' | '/crm/proposals/$proposalId'
  edit: '/proposals/$proposalId/edit' | '/crm/proposals/$proposalId/edit'
  review: '/proposals/$proposalId/review' | '/crm/proposals/$proposalId/review'
  /** The first breadcrumb: the app home, or the CRM. */
  homeCrumb: { label: string; to: '/' | '/crm' }
}

export const WORKSPACE_PROPOSAL_PATHS: ProposalPaths = {
  list: '/proposals',
  create: '/proposals/new',
  approvals: '/proposals/approvals',
  detail: '/proposals/$proposalId',
  edit: '/proposals/$proposalId/edit',
  review: '/proposals/$proposalId/review',
  homeCrumb: { label: 'Home', to: '/' },
}

export const CRM_PROPOSAL_PATHS: ProposalPaths = {
  list: '/crm/proposals',
  create: '/crm/proposals/new',
  approvals: '/crm/proposals/approvals',
  detail: '/crm/proposals/$proposalId',
  edit: '/crm/proposals/$proposalId/edit',
  review: '/crm/proposals/$proposalId/review',
  homeCrumb: { label: 'CRM', to: '/crm' },
}

const ProposalPathsContext = createContext<ProposalPaths>(WORKSPACE_PROPOSAL_PATHS)

/** The paths for the proposal tree the current page is mounted in. Defaults to `/proposals`. */
export function useProposalPaths(): ProposalPaths {
  return useContext(ProposalPathsContext)
}

export function ProposalPathsProvider({ paths, children }: { paths: ProposalPaths; children: ReactNode }) {
  return <ProposalPathsContext.Provider value={paths}>{children}</ProposalPathsContext.Provider>
}
